/* Bindings between the Finance & Operations workspace pages and the F&O backend (through the gateway).
   Each binding turns API records into the page's Row shape (load), and page edits / buttons into API calls.
   The hidden field _gid on a row holds the backend id. Finance pages (ledger, AP/AR, bank, budgets ...) are NOT here:
   they belong to the Cilpronacc API and stay on local demo data until that API is connected. */
import { api, Rec } from "./Api/Api";
import { ACC_BINDINGS } from "./Accops";

export type Val = string | number;
export interface Row {
  id: string;
  [k: string]: Val;
}
type Vals = Record<string, Val>;

export interface Ctx {
  site: string;
  partners: Rec[];
  items: Rec[];
  locations: Rec[];
  workers: Rec[];
  leaveTypes: Rec[];
}
export interface Binding {
  load: (c: Ctx) => Promise<Row[]>;
  create?: (v: Vals, c: Ctx) => Promise<void>;
  update?: (r: Row, v: Vals, c: Ctx) => Promise<void>;
  remove?: (r: Row, c: Ctx) => Promise<void>;
  /** page button label -> API call; returns the message to show */
  act?: Record<string, (r: Row, c: Ctx) => Promise<string>>;
  /** why New is unavailable (documents the backend creates itself) */
  noCreate?: string;
}

/* ---------------- helpers ---------------- */
const S = (x: unknown): string => (x === null || x === undefined ? "" : String(x));
const N = (x: unknown): number => Number(x) || 0;
const D = (x: unknown): string => S(x).slice(0, 10);
const gid = (r: Row): string => S(r._gid);
const today = (): string => new Date().toISOString().slice(0, 10);
const days = (t: string): number => Number(/\d+/.exec(t)?.[0] ?? 0);
const lines = (e: Rec): Rec[] => (Array.isArray(e.lines) ? (e.lines as Rec[]) : []);
const nextCode = (prefix: string, existing: string[]): string => {
  const mx = existing.reduce((m, c) => (c.startsWith(prefix) ? Math.max(m, Number(c.slice(prefix.length)) || 0) : m), 0);
  return prefix + String(mx + 1).padStart(4, "0");
};
const byName = (list: Rec[], name: unknown, key = "name"): Rec | undefined => list.find((x) => S(x[key]) === S(name));
const byId = (list: Rec[], id: unknown): Rec | undefined => list.find((x) => x.id === id);
const itemByCode = (c: Ctx, code: string): Rec | undefined => c.items.find((i) => S(i.code) === code);
const numberOf = (e: Rec, prefix: string, existing: string[] = []): string => S(e.number) || S(e.code) || nextCode(prefix, existing);
const row = (e: Rec, id: string, o: Vals): Row => ({ id, _gid: e.id, ...o });

/** fetch GET /{id} for every record (list endpoints return the header only; lines come with the single read) */
async function detail(path: string, list: Rec[]): Promise<Rec[]> {
  return Promise.all(list.slice(0, 80).map((e) => api.get(`${path}/${e.id}`).catch(() => e)));
}
/** read -> change -> write back (PUT overwrites everything, so send the full record) */
async function change(path: string, id: string, fn: (e: Rec) => void): Promise<Rec> {
  const e = await api.get(`${path}/${id}`);
  fn(e);
  return api.put(`${path}/${id}`, e);
}
const nm = (c: Ctx, id: unknown): string => S(byId(c.partners, id)?.name);

/* ---------------- shared status vocabularies (backend -> page) ---------------- */
const docStatus = (s: string): string =>
  ({ PendingApproval: "Submitted", Sent: "Confirmed", Approved: "Confirmed", PartiallyReceived: "Confirmed", Received: "Received", Closed: "Received", Rejected: "Rejected", Cancelled: "Cancelled", Draft: "Draft" } as Record<string, string>)[s] ?? s;

/* =========================================================
   Vendors and customers (Master data partners)
   ========================================================= */
const partnerStatus = (p: Rec): string => ({ Active: "Active", Blocked: "On hold", Draft: "Draft", Obsolete: "Inactive", Merged: "Inactive" } as Record<string, string>)[S(p.status)] ?? S(p.status);
const partnerBody = (v: Vals, kind: "Supplier" | "Customer", code: string): Rec =>
  ({ code, name: S(v.name), legalName: S(v.name), kind, partnerGroup: S(v.group), paymentTermsDays: days(S(v.terms)), phone: S(v.phone), countryCode: "NG", currencyCode: "NGN", taxId: `TIN-${code}`, email: `${code.toLowerCase()}@example.invalid`, ...(kind === "Customer" ? { creditLimit: N(v.limit) } : {}) } as unknown as Rec);

const partners = (kind: "Supplier" | "Customer"): Binding => ({
  load: async (c) => c.partners.filter((p) => S(p.kind) === kind || S(p.kind) === "Both").map((p) => row(p, S(p.code), { name: S(p.name), group: S(p.partnerGroup), terms: `Net ${N(p.paymentTermsDays)}`, phone: S(p.phone), limit: N(p.creditLimit), owed: 0, status: partnerStatus(p) })),
  create: async (v, c) => {
    const code = nextCode(kind === "Supplier" ? "V-" : "C-", c.partners.map((p) => S(p.code)));
    const p = await api.post("api/masterdata/partners", partnerBody(v, kind, code));
    await api.post(`api/masterdata/partners/${p.id}/activate`).catch(() => undefined);
  },
  update: async (r, v) => {
    await change("api/masterdata/partners", gid(r), (e) => Object.assign(e, { name: S(v.name), legalName: S(v.name), partnerGroup: S(v.group), paymentTermsDays: days(S(v.terms)), ...(kind === "Supplier" ? { phone: S(v.phone) } : { creditLimit: N(v.limit) }) }));
  },
  remove: async (r) => api.del(`api/masterdata/partners/${gid(r)}`),
});

/* =========================================================
   Procurement
   ========================================================= */
const reqStatus = (s: string): string => (s === "Converted" ? "Approved" : s);
const reqs: Binding = {
  load: async (c) => {
    const list = await detail("api/procurement/requisitions", await api.all("api/procurement/requisitions"));
    return list.map((e) => {
      const l = lines(e)[0];
      return row(e, S(e.number), { requester: S(e.requester), vendor: nm(c, l?.suggestedSupplierId), description: S(e.justification) || S(l?.description), date: D(e.neededBy), amount: N(e.estimatedTotal), status: reqStatus(S(e.status)) });
    });
  },
  create: async (v, c) => {
    const item = itemByCode(c, "PUR-GENERAL");
    await api.post("api/procurement/requisitions", { requester: S(v.requester), neededBy: S(v.date), justification: S(v.description), currencyCode: "NGN", lines: [{ lineNo: 1, itemId: item?.id, description: S(v.description), quantity: 1, uoM: "EA", estimatedUnitPrice: N(v.amount), suggestedSupplierId: byName(c.partners, v.vendor)?.id ?? null, siteId: c.site }] });
  },
  update: async (r, v, c) => {
    await change("api/procurement/requisitions", gid(r), (e) => {
      Object.assign(e, { requester: S(v.requester), neededBy: S(v.date), justification: S(v.description) });
      const l = lines(e)[0];
      if (l) Object.assign(l, { description: S(v.description), estimatedUnitPrice: N(v.amount), suggestedSupplierId: byName(c.partners, v.vendor)?.id ?? null });
    });
  },
  remove: async (r) => api.del(`api/procurement/requisitions/${gid(r)}`),
  act: {
    Submit: async (r) => (await api.post(`api/procurement/requisitions/${gid(r)}/submit`), `${r.id} submitted`),
    Approve: async (r) => (await api.post(`api/procurement/requisitions/${gid(r)}/decision`, { approve: true, comments: "" }), `${r.id} approved`),
    Reject: async (r) => (await api.post(`api/procurement/requisitions/${gid(r)}/decision`, { approve: false, comments: "Rejected" }), `${r.id} rejected`),
    "Convert to PO": async (r) => (await api.post(`api/procurement/requisitions/${gid(r)}/convert`), `${r.id} converted to purchase order(s)`),
  },
};

const rfqStatus = (s: string): string => ({ Published: "Sent", Awarded: "Closed", Closed: "Closed" } as Record<string, string>)[s] ?? s;
const rfqs: Binding = {
  load: async () => {
    const list = await detail("api/procurement/rfqs", await api.all("api/procurement/rfqs"));
    return Promise.all(
      list.map(async (e) => {
        const bids = await api.get<Rec[] | { items: Rec[] }>(`api/procurement/rfqs/${e.id}/bids`).then((b) => (Array.isArray(b) ? b : b.items ?? [])).catch(() => [] as Rec[]);
        const best = [...bids].sort((a, b) => N(a.totalPrice) - N(b.totalPrice))[0];
        const l = lines(e)[0];
        const st = bids.length > 0 && S(e.status) === "Published" ? "Received" : rfqStatus(S(e.status));
        return row(e, S(e.number), { vendor: S(best?.supplierName), description: S(e.title), date: D(e.openFrom), amount: best ? N(best.totalPrice) : N(l?.targetPrice) * N(l?.quantity), status: st });
      }),
    );
  },
  create: async (v, c) => {
    const item = itemByCode(c, "PUR-GENERAL");
    const open = S(v.date) || today();
    await api.post("api/procurement/rfqs", { type: "Rfq", title: S(v.description), openFrom: `${open}T00:00:00Z`, closeAt: `${new Date(Date.parse(open) + 14 * 864e5).toISOString().slice(0, 10)}T00:00:00Z`, currencyCode: "NGN", lines: [{ lineNo: 1, itemId: item?.id, description: S(v.description), quantity: 1, uoM: "EA", targetPrice: N(v.amount), siteId: c.site }] });
  },
  remove: async (r) => api.del(`api/procurement/rfqs/${gid(r)}`),
  act: {
    Send: async (r) => (await api.post(`api/procurement/rfqs/${gid(r)}/publish`), `${r.id} sent to suppliers`),
    "Quote received": async (r, c) => {
      const sup = byName(c.partners, r.vendor);
      if (!sup) throw new Error("Pick the vendor on the RFQ (Edit) before recording their quote.");
      const e = await api.get(`api/procurement/rfqs/${gid(r)}`);
      await api.post(`api/procurement/rfqs/${gid(r)}/bids`, { supplierId: sup.id, leadTimeDays: 7, lines: lines(e).map((l) => ({ rfqLineId: l.id, unitPrice: N(r.amount) / Math.max(1, N(l.quantity)), quantityOffered: l.quantity })) });
      return `Quote recorded on ${r.id}`;
    },
    Close: async (r) => (await api.post(`api/procurement/rfqs/${gid(r)}/close`), `${r.id} closed`),
  },
};

const pos: Binding = {
  load: async (c) => {
    const list = await detail("api/procurement/purchase-orders", await api.all("api/procurement/purchase-orders"));
    return list.map((e) => row(e, S(e.number), { vendor: S(e.supplierName) || nm(c, e.supplierId), description: S(e.notes) || S(lines(e)[0]?.description), date: D(e.orderDate), amount: N(e.total), status: docStatus(S(e.status)) }));
  },
  create: async (v, c) => {
    const sup = byName(c.partners, v.vendor);
    if (!sup) throw new Error("Choose a vendor.");
    await api.post("api/procurement/purchase-orders", { supplierId: sup.id, supplierName: sup.name, currencyCode: "NGN", orderDate: S(v.date), notes: S(v.description), shipToSiteId: c.site, lines: [{ lineNo: 1, itemId: itemByCode(c, "PUR-GENERAL")?.id, description: S(v.description), quantity: 1, uoM: "EA", unitPrice: N(v.amount), taxRate: 0 }] });
  },
  update: async (r, v, c) => {
    await change("api/procurement/purchase-orders", gid(r), (e) => {
      const sup = byName(c.partners, v.vendor);
      Object.assign(e, { supplierId: sup?.id ?? e.supplierId, supplierName: sup?.name ?? e.supplierName, orderDate: S(v.date), notes: S(v.description) });
      const l = lines(e)[0];
      if (l) Object.assign(l, { description: S(v.description), unitPrice: N(v.amount) });
    });
  },
  remove: async (r) => api.del(`api/procurement/purchase-orders/${gid(r)}`),
  act: {
    Confirm: async (r) => {
      const s = await api.post(`api/procurement/purchase-orders/${gid(r)}/submit`);
      if (S(s.status) === "Approved") {
        await api.post(`api/procurement/purchase-orders/${gid(r)}/send`);
        return `${r.id} confirmed with ${S(r.vendor)}`;
      }
      return `${r.id} submitted - waiting for an approver (${S(s.status)})`;
    },
    "Receive goods": async (r, c) => {
      const e = await api.get(`api/procurement/purchase-orders/${gid(r)}`);
      const wh = c.locations.find((l) => S(l.code) === "WH-0001");
      await api.post(`api/procurement/purchase-orders/${gid(r)}/receive`, { siteId: c.site, warehouseId: wh?.id ?? null, receiptDate: today(), lines: lines(e).map((l) => ({ poLineId: l.id, quantity: Math.max(0, N(l.quantity) - N(l.receivedQty)) })).filter((l) => l.quantity > 0) });
      return `${r.id} received into inventory`;
    },
    "Cancel order": async (r) => (await api.post(`api/procurement/purchase-orders/${gid(r)}/cancel`, { reason: "Cancelled from workspace" }), `${r.id} cancelled`),
  },
};

/* =========================================================
   Sales
   ========================================================= */
const quoteStatus = (s: string): string => ({ Accepted: "Won", Rejected: "Lost", Expired: "Lost" } as Record<string, string>)[s] ?? s;
const quotes: Binding = {
  load: async () => {
    const list = await detail("api/sales/quotes", await api.all("api/sales/quotes"));
    return list.map((e) => row(e, S(e.number), { customer: S(e.customerName), description: S(e.notes) || S(lines(e)[0]?.description), date: D(e.quoteDate), amount: N(e.total), status: quoteStatus(S(e.status)) }));
  },
  create: async (v, c) => {
    const cu = byName(c.partners, v.customer);
    if (!cu) throw new Error("Choose a customer.");
    await api.post("api/sales/quotes", { customerId: cu.id, currencyCode: "NGN", quoteDate: S(v.date), validUntil: new Date(Date.parse(S(v.date) || today()) + 30 * 864e5).toISOString().slice(0, 10), siteId: c.site, notes: S(v.description), lines: [{ itemId: itemByCode(c, "SVC-EVENT")?.id, quantity: 1, uoM: "EA", unitPrice: N(v.amount), discountPct: 0 }] });
  },
  update: async (r, v, c) => {
    await change("api/sales/quotes", gid(r), (e) => {
      Object.assign(e, { customerId: byName(c.partners, v.customer)?.id ?? e.customerId, quoteDate: S(v.date), notes: S(v.description) });
      const l = lines(e)[0];
      if (l) l.unitPrice = N(v.amount);
    });
  },
  remove: async (r) => api.del(`api/sales/quotes/${gid(r)}`),
  act: {
    Send: async (r) => (await api.post(`api/sales/quotes/${gid(r)}/send`), `${r.id} sent`),
    "Mark won": async (r) => (await api.post(`api/sales/quotes/${gid(r)}/accept`), `${r.id} won`),
    "Mark lost": async (r) => (await api.post(`api/sales/quotes/${gid(r)}/reject`), `${r.id} lost`),
    "Convert to order": async (r) => (await api.post(`api/sales/quotes/${gid(r)}/convert`), `${r.id} converted to a sales order`),
  },
};

const sos: Binding = {
  load: async () => {
    const list = await detail("api/sales/orders", await api.all("api/sales/orders"));
    return list.map((e) => row(e, S(e.number), { customer: S(e.customerName), description: S(e.notes) || S(lines(e)[0]?.itemName), date: D(e.orderDate), amount: N(e.total), status: docStatus(S(e.status)) === "Confirmed" || S(e.status) === "PartiallyShipped" ? "Confirmed" : S(e.status) === "Shipped" || S(e.status) === "Invoiced" ? "Shipped" : docStatus(S(e.status)) }));
  },
  create: async (v, c) => {
    const cu = byName(c.partners, v.customer);
    if (!cu) throw new Error("Choose a customer.");
    await api.post("api/sales/orders", { customerId: cu.id, currencyCode: "NGN", orderDate: S(v.date), siteId: c.site, channel: "Direct", notes: S(v.description), lines: [{ itemId: itemByCode(c, "SVC-EVENT")?.id, quantity: 1, uoM: "EA", unitPrice: N(v.amount), discountPct: 0 }] });
  },
  update: async (r, v, c) => {
    await change("api/sales/orders", gid(r), (e) => {
      Object.assign(e, { customerId: byName(c.partners, v.customer)?.id ?? e.customerId, orderDate: S(v.date), notes: S(v.description) });
      const l = lines(e)[0];
      if (l) l.unitPrice = N(v.amount);
    });
  },
  remove: async (r) => api.del(`api/sales/orders/${gid(r)}`),
  act: {
    Confirm: async (r) => {
      const s = await api.post(`api/sales/orders/${gid(r)}/submit`);
      return S(s.status) === "Confirmed" ? `${r.id} confirmed` : `${r.id} submitted - status ${S(s.status)}`;
    },
    Ship: async (r) => (await api.post(`api/sales/orders/${gid(r)}/ship`, { shipDate: today() }), `${r.id} shipped`),
    "Cancel order": async (r) => (await api.post(`api/sales/orders/${gid(r)}/cancel`, { reason: "Cancelled from workspace" }), `${r.id} cancelled`),
  },
};

const rmaStatus = (s: string): string => ({ Requested: "Draft", Received: "Approved", InTransit: "Approved" } as Record<string, string>)[s] ?? s;
const returns: Binding = {
  load: async () => (await api.all("api/sales/rmas")).map((e) => row(e, S(e.number), { customer: S(e.customerName), reason: S(e.reason), date: D(e.requestDate), amount: N(e.creditAmount), status: rmaStatus(S(e.status)) })),
  create: async (v, c) => {
    const cu = byName(c.partners, v.customer);
    const order = (await api.all("api/sales/orders", cu ? { customerId: S(cu.id) } : undefined))[0];
    if (!order) throw new Error("This customer has no sales order to return against.");
    const o = await api.get(`api/sales/orders/${order.id}`);
    const l = lines(o)[0];
    await api.post("api/sales/rmas", { orderId: order.id, reason: S(v.reason), requestDate: S(v.date), lines: l ? [{ orderLineId: l.id, quantity: 1, disposition: "Restock" }] : [] });
  },
  remove: async (r) => api.del(`api/sales/rmas/${gid(r)}`),
  act: {
    Approve: async (r) => (await api.post(`api/sales/rmas/${gid(r)}/approve`, { comments: "" }), `${r.id} approved`),
    "Issue credit": async (r) => (await api.post(`api/sales/rmas/${gid(r)}/credit`), `${r.id} credited`),
  },
};

const prices: Binding = {
  load: async () => {
    const list = await detail("api/sales/price-lists", await api.all("api/sales/price-lists"));
    return list.flatMap((pl) =>
      lines(pl).map((l, i) => ({ id: S(l.reference) || `${S(pl.code)}-${i + 1}`, _gid: pl.id, _line: S(l.id), item: S(l.itemCode), price: N(l.unitPrice), from: D(pl.validFrom), to: D(pl.validTo), status: pl.isActive === false ? "Inactive" : "Active" } as Row)),
    );
  },
  update: async (r, v, c) => {
    await change("api/sales/price-lists", gid(r), (e) => {
      const l = lines(e).find((x) => S(x.id) === S(r._line));
      if (l) l.unitPrice = N(v.price);
      Object.assign(e, { validFrom: S(v.from) || null, validTo: S(v.to) || null });
    });
    void c;
  },
  create: async (v, c) => {
    const it = itemByCode(c, S(v.item)) ?? c.items.find((i) => S(i.name) === S(v.item));
    if (!it) throw new Error("Type an item code or name that exists in Inventory.");
    const pl = (await api.all("api/sales/price-lists")).find((p) => S(p.code) === "PL-HOSP");
    const e = pl ? await api.get(`api/sales/price-lists/${pl.id}`) : null;
    if (!e) throw new Error("Price list PL-HOSP is missing - seed the demo data first.");
    lines(e).push({ id: "", itemId: it.id, itemCode: it.code, reference: nextCode("PL-", lines(e).map((l) => S(l.reference))), minQuantity: 0, unitPrice: N(v.price), uoM: S(it.baseUoM) } as unknown as Rec);
    await api.put(`api/sales/price-lists/${e.id}`, e);
  },
  act: {
    Deactivate: async (r) => (await change("api/sales/price-lists", gid(r), (e) => (e.isActive = false)), `${r.id} deactivated`),
    Activate: async (r) => (await change("api/sales/price-lists", gid(r), (e) => (e.isActive = true)), `${r.id} activated`),
  },
};

/* =========================================================
   Inventory and warehouse
   ========================================================= */
const locName = (c: Ctx, id: unknown): string => S(byId(c.locations, id)?.name);
const whs: Binding = {
  load: async (c) => c.locations.filter((l) => S(l.kind) === "Warehouse").map((l) => row(l, S(l.code), { name: S(l.name), type: S(l.storeType), location: S(l.placement), status: l.isActive === false ? "Inactive" : "Active" })),
  create: async (v, c) => {
    await api.post("api/masterdata/locations", { siteId: c.site, code: nextCode("WH-", c.locations.map((l) => S(l.code))), name: S(v.name), kind: "Warehouse", storeType: S(v.type), placement: S(v.location), isActive: true });
  },
  update: async (r, v) => void (await change("api/masterdata/locations", gid(r), (e) => Object.assign(e, { name: S(v.name), storeType: S(v.type), placement: S(v.location) }))),
  remove: async (r) => api.del(`api/masterdata/locations/${gid(r)}`),
  act: {
    Deactivate: async (r) => (await change("api/masterdata/locations", gid(r), (e) => (e.isActive = false)), `${r.id} deactivated`),
    Activate: async (r) => (await change("api/masterdata/locations", gid(r), (e) => (e.isActive = true)), `${r.id} activated`),
  },
};

async function move(c: Ctx, kind: "Receipt" | "Issue", itemId: unknown, warehouseId: unknown, quantity: number, unitCost: number, reason: string): Promise<void> {
  await api.post("api/inventory/transactions", { kind, postingDate: today(), reason, lines: [{ itemId, quantity, siteId: c.site, warehouseId, unitCost: kind === "Receipt" ? unitCost : null, currencyCode: "NGN" }] });
}

const inventory: Binding = {
  load: async (c) => {
    const [bal, pol] = await Promise.all([api.all("api/inventory/stock"), api.all("api/inventory/reorder-policies")]);
    return c.items
      .filter((i) => i.isStocked === true || S(i.itemType) === "Stocked")
      .map((i) => {
        const mine = bal.filter((b) => b.itemId === i.id);
        const main = [...mine].sort((a, b) => N(b.onHand) - N(a.onHand))[0];
        return row(i, S(i.code), { name: S(i.name), warehouse: locName(c, main?.warehouseId), _wh: S(main?.warehouseId), qty: mine.reduce((t, b) => t + N(b.onHand), 0), cost: N(i.standardCost), reorder: N(pol.find((p) => p.itemId === i.id)?.reorderPoint) });
      });
  },
  create: async (v, c) => {
    const code = nextCode("IT-", c.items.map((i) => S(i.code)));
    const it = await api.post("api/masterdata/items", { code, name: S(v.name), itemType: "Stocked", baseUoM: "EA", standardCost: N(v.cost), costCurrency: "NGN", costingMethod: "Average", isPurchasable: true, isSellable: false, tracking: "None", gtin: `GTIN${Date.now()}`.slice(0, 14) });
    await api.post(`api/masterdata/items/${it.id}/activate`).catch(() => undefined);
    const wh = byName(c.locations, v.warehouse);
    if (wh && N(v.qty) > 0) await move(c, "Receipt", it.id, wh.id, N(v.qty), N(v.cost), "Opening stock");
    if (N(v.reorder) > 0) await api.post("api/inventory/reorder-policies", { itemId: it.id, itemCode: code, siteId: c.site, method: "ReorderPoint", enabled: true, reorderPoint: N(v.reorder) }).catch(() => undefined);
  },
  update: async (r, v, c) => {
    await change("api/masterdata/items", gid(r), (e) => Object.assign(e, { name: S(v.name), standardCost: N(v.cost) }));
    const diff = N(v.qty) - N(r.qty);
    const wh = byName(c.locations, v.warehouse) ?? byId(c.locations, r._wh);
    if (diff !== 0 && wh) await move(c, diff > 0 ? "Receipt" : "Issue", r._gid, wh.id, Math.abs(diff), N(v.cost), "Adjusted from workspace");
    const pol = (await api.all("api/inventory/reorder-policies")).find((p) => p.itemId === r._gid);
    if (pol) await change("api/inventory/reorder-policies", S(pol.id), (e) => (e.reorderPoint = N(v.reorder)));
  },
  remove: async (r) => api.del(`api/masterdata/items/${gid(r)}`),
  act: {
    "Receive 10": async (r, c) => (await move(c, "Receipt", r._gid, r._wh, 10, N(r.cost), "Receipt from workspace"), `10 added to ${S(r.name)}`),
    "Issue 10": async (r, c) => (await move(c, "Issue", r._gid, r._wh, Math.min(10, N(r.qty)), N(r.cost), "Issue from workspace"), `${Math.min(10, N(r.qty))} issued from ${S(r.name)}`),
  },
};

const trStatus = (s: string): string => ({ Shipped: "In transit", PartiallyReceived: "In transit", ClosedWithLoss: "Received", PendingApproval: "Submitted" } as Record<string, string>)[s] ?? s;
const stockTransfers: Binding = {
  load: async (c) => {
    const list = await detail("api/inventory/transfers", await api.all("api/inventory/transfers"));
    return list.map((e) => row(e, S(e.number), { itemName: S(lines(e)[0]?.itemCode) ? S(byId(c.items, lines(e)[0]?.itemId)?.name) || S(lines(e)[0]?.itemCode) : "", toWh: locName(c, e.toWarehouseId), date: D(e.requestedDate), status: trStatus(S(e.status)) }));
  },
  create: async (v, c) => {
    const it = c.items.find((i) => S(i.code) === S(v.item) || i.id === v.item);
    const to = byName(c.locations, v.toWh);
    if (!it || !to) throw new Error("Choose an item and a destination warehouse.");
    const bal = (await api.all("api/inventory/stock", { itemId: S(it.id) })).sort((a, b) => N(b.onHand) - N(a.onHand))[0];
    if (!bal || N(bal.onHand) <= 0) throw new Error(`${S(it.name)} has no stock to move.`);
    if (bal.warehouseId === to.id) throw new Error("The item is already in that warehouse.");
    await api.post("api/inventory/transfers", { fromSiteId: c.site, fromWarehouseId: bal.warehouseId, toSiteId: c.site, toWarehouseId: to.id, requestedDate: S(v.date), lines: [{ itemId: it.id, itemCode: it.code, uoM: S(it.baseUoM) || "EA", quantity: N(bal.onHand) }] });
  },
  remove: async (r) => api.del(`api/inventory/transfers/${gid(r)}`),
  act: {
    Ship: async (r) => (await api.post(`api/inventory/transfers/${gid(r)}/ship`), `${r.id} shipped`),
    Receive: async (r) => (await api.post(`api/inventory/transfers/${gid(r)}/receive`, {}), `${r.id} received`),
  },
};

const counts: Binding = {
  load: async (c) => {
    const list = await detail("api/inventory/cycle-counts", await api.all("api/inventory/cycle-counts"));
    return list.map((e) => {
      const l = lines(e)[0];
      return row(e, S(e.number), { itemName: S(l?.itemName) || S(byId(c.items, l?.itemId)?.name), date: D(e.plannedDate), book: N(l?.systemQuantity), counted: l?.countedQuantity == null ? 0 : N(l.countedQuantity), variance: l?.countedQuantity == null ? 0 : N(l.countedQuantity) - N(l?.systemQuantity), status: S(e.status) === "Posted" ? "Completed" : S(e.status) === "Draft" || S(e.status) === "InProgress" || S(e.status) === "Counted" ? "Draft" : S(e.status) === "PendingApproval" ? "Submitted" : S(e.status) });
    });
  },
  create: async (v, c) => {
    const it = c.items.find((i) => S(i.code) === S(v.item) || i.id === v.item);
    if (!it) throw new Error("Choose an item.");
    const bal = (await api.all("api/inventory/stock", { itemId: S(it.id) })).sort((a, b) => N(b.onHand) - N(a.onHand))[0];
    if (!bal) throw new Error(`${S(it.name)} has no stock record to count.`);
    const cc = await api.post("api/inventory/cycle-counts", { siteId: c.site, warehouseId: bal.warehouseId, name: `Count ${S(it.name)}`, plannedDate: S(v.date) });
    await api.post(`api/inventory/cycle-counts/${cc.id}/start`).catch(() => undefined);
    const withLine = await api.post(`api/inventory/cycle-counts/${cc.id}/lines`, { itemId: it.id, warehouseId: bal.warehouseId, binCode: S(bal.binCode), lotNo: S(bal.lotNo) });
    const l = lines(withLine)[0];
    if (l && S(v.counted) !== "") await api.post(`api/inventory/cycle-counts/${cc.id}/count`, { counts: [{ lineId: l.id, countedQuantity: N(v.counted) }] });
  },
  act: {
    "Post count": async (r) => {
      const id = gid(r);
      let e = await api.get(`api/inventory/cycle-counts/${id}`);
      if (S(e.status) === "Draft") e = await api.post(`api/inventory/cycle-counts/${id}/start`);
      const l = lines(e)[0];
      if (l && l.countedQuantity == null) await api.post(`api/inventory/cycle-counts/${id}/count`, { counts: [{ lineId: l.id, countedQuantity: N(r.counted) }] });
      e = await api.post(`api/inventory/cycle-counts/${id}/submit`, { allowPartial: true });
      if (S(e.status) === "PendingApproval") return `${r.id} sent for approval (variance needs an approver)`;
      return `${r.id} posted`;
    },
  },
};

const pickStatus = (s: string): string => ({ Released: "Open", Picking: "Open", Dispatched: "Packed", PartiallyShipped: "Packed" } as Record<string, string>)[s] ?? s;
const picks: Binding = {
  noCreate: "Pick lists are created by the warehouse when a sales order is confirmed.",
  load: async (c) => (await api.all("api/warehouse/outbound")).map((e) => row(e, S(e.number), { order: S(e.sourceNumber), warehouse: locName(c, e.facilityId) || S(e.shipToName), date: D(e.requestedDate), status: pickStatus(S(e.status)) })),
  act: {
    Pick: async (r) => (await api.post(`api/warehouse/outbound/${gid(r)}/release`), `${r.id} released for picking`),
    Pack: async (r) => (await api.post(`api/warehouse/outbound/${gid(r)}/dispatch`, {}), `${r.id} dispatched`),
  },
};

/* =========================================================
   Production
   ========================================================= */
const boms: Binding = {
  load: async (c) => {
    const list = await detail("api/manufacturing/boms", await api.all("api/manufacturing/boms"));
    return list.map((e) => {
      const cost = lines(e).reduce((t, l) => t + N(l.quantity) * N(byId(c.items, l.componentItemId)?.standardCost), 0);
      return row(e, S(e.number) || S(e.itemCode), { name: S(e.itemName), yieldQty: N(e.quantityPer), cost, status: S(e.status) === "Active" ? "Active" : S(e.status) === "Draft" ? "Draft" : "Inactive" });
    });
  },
  act: {
    Deactivate: async (r) => (await api.post(`api/manufacturing/boms/${gid(r)}/obsolete`), `${r.id} deactivated`),
    Activate: async (r) => (await api.post(`api/manufacturing/boms/${gid(r)}/activate`), `${r.id} activated`),
  },
  noCreate: "Recipes (bills of material) with their component lines are built in the Bills of material resource page.",
};

const moStatus = (s: string): string => ({ Draft: "Planned", Planned: "Planned", Released: "Planned", InProgress: "Started", Completed: "Finished", Closed: "Finished" } as Record<string, string>)[s] ?? s;
const prodOrders: Binding = {
  load: async () => {
    const bomList = await api.all("api/manufacturing/boms");
    return (await api.all("api/manufacturing/production-orders")).map((e) => {
      const b = bomList.find((x) => x.id === e.bomId);
      return row(e, S(e.number), { bom: S(b?.itemName) || S(e.itemName), batches: N(e.quantity), total: N(e.plannedCost), date: D(e.startDate), status: moStatus(S(e.status)) });
    });
  },
  create: async (v, c) => {
    const b = (await api.all("api/manufacturing/boms")).find((x) => S(x.itemName) === S(v.bom) || x.id === v.bom);
    if (!b) throw new Error("Choose a recipe.");
    await api.post("api/manufacturing/production-orders", { itemId: b.itemId, siteId: c.site, quantity: N(v.batches) * N(b.quantityPer || 1), startDate: S(v.date), dueDate: S(v.date) });
  },
  act: {
    Start: async (r) => (await api.post(`api/manufacturing/production-orders/${gid(r)}/release`, {}), `${r.id} released to the floor`),
    Finish: async (r) => (await api.post(`api/manufacturing/production-orders/${gid(r)}/complete`, { quantityGood: N(r.batches), force: false }), `${r.id} completed`),
  },
};

/* =========================================================
   Projects, people, maintenance, organisation
   ========================================================= */
const prjStatus = (s: string): string => ({ Draft: "Draft", PendingApproval: "Submitted", Active: "In progress", Completed: "Completed", Closed: "Completed", OnHold: "On hold" } as Record<string, string>)[s] ?? s;
const projects: Binding = {
  load: async () => (await api.all("api/projects/projects")).map((e) => row(e, S(e.code), { name: S(e.name), customer: S(e.customerName), budget: N(e.contractValue), cost: N(e.actualCost), start: D(e.startDate), status: prjStatus(S(e.status)) })),
  create: async (v, c) => {
    const cu = byName(c.partners, v.customer);
    const p = await api.post("api/projects/projects", { code: nextCode("PJ-", (await api.all("api/projects/projects")).map((x) => S(x.code))), name: S(v.name), customerId: cu?.id ?? null, customerCode: cu?.code ?? null, customerName: cu?.name ?? null, siteId: c.site, contractType: "FixedPrice", recognitionMethod: "PercentComplete", currencyCode: "NGN", contractValue: N(v.budget), startDate: S(v.date ?? v.start) });
    await api.post(`api/projects/projects/${p.id}/submit`).then(() => api.post(`api/projects/projects/${p.id}/approve`, { comments: "" })).catch(() => undefined);
  },
  update: async (r, v, c) => void (await change("api/projects/projects", gid(r), (e) => Object.assign(e, { name: S(v.name), customerId: byName(c.partners, v.customer)?.id ?? e.customerId, contractValue: N(v.budget), startDate: S(v.start) }))),
  remove: async (r) => api.del(`api/projects/projects/${gid(r)}`),
  act: { Complete: async (r) => (await api.post(`api/projects/projects/${gid(r)}/complete`), `${r.id} completed`) },
};

const wName = (c: Ctx, id: unknown): string => S(byId(c.workers, id)?.fullName);
const tsStatus = (s: string): string => (s === "Draft" ? "Submitted" : s === "Reversed" ? "Rejected" : s);
const timesheets: Binding = {
  load: async (c) => {
    const list = await detail("api/workforce/timesheets", await api.all("api/workforce/timesheets"));
    return list.map((e) => row(e, S(e.number), { employee: S(e.workerName) || wName(c, e.workerId), project: S(lines(e)[0]?.projectCode), date: D(lines(e)[0]?.workDate) || D(e.periodStart), hours: N(e.totalHours) || N(e.regularHours), cost: N(e.totalCost), status: tsStatus(S(e.status)) }));
  },
  create: async (v, c) => {
    const w = byName(c.workers, v.employee, "fullName");
    if (!w) throw new Error("Choose an employee.");
    const proj = (await api.all("api/projects/projects")).find((p) => S(p.code) === S(v.project));
    const t = await api.post("api/workforce/timesheets", { workerId: w.id, periodStart: S(v.date), notes: "", lines: [{ workDate: S(v.date), hours: N(v.hours), projectId: proj?.id ?? null, projectCode: proj ? S(proj.code) : null, billable: !!proj }] });
    await api.post(`api/workforce/timesheets/${t.id}/submit`).catch(() => undefined);
  },
  remove: async (r) => api.del(`api/workforce/timesheets/${gid(r)}`),
  act: {
    Approve: async (r) => (await api.post(`api/workforce/timesheets/${gid(r)}/approve`, { comments: "" }), `${r.id} approved`),
    Reject: async (r) => (await api.post(`api/workforce/timesheets/${gid(r)}/reject`, { comments: "Rejected" }), `${r.id} rejected`),
  },
};

const workers: Binding = {
  load: async (c) => c.workers.map((w) => row(w, S(w.code), { name: S(w.fullName), department: S(w.departmentCode) || S(w.orgUnitCode), position: S(w.jobTitle), hired: D(w.hireDate), salary: N(w.monthlySalary), status: S(w.status) === "Terminated" ? "Terminated" : S(w.status) === "Suspended" ? "On hold" : "Active" })),
  create: async (v, c) => {
    await api.post("api/workforce/workers", { code: nextCode("W-", c.workers.map((w) => S(w.code))), fullName: S(v.name), siteId: c.site, departmentCode: S(v.department), jobTitle: S(v.position), type: "Employee", hireDate: S(v.hired), currencyCode: "NGN", monthlySalary: N(v.salary), hourlyRate: Math.round(N(v.salary) / 173), weeklyHours: 40, overtimeMultiplier: 1.5 });
  },
  update: async (r, v) => void (await change("api/workforce/workers", gid(r), (e) => Object.assign(e, { fullName: S(v.name), departmentCode: S(v.department), jobTitle: S(v.position), hireDate: S(v.hired), monthlySalary: N(v.salary), hourlyRate: Math.round(N(v.salary) / 173) }))),
  remove: async (r) => api.del(`api/workforce/workers/${gid(r)}`),
  act: {
    Terminate: async (r) => (await api.post(`api/workforce/workers/${gid(r)}/terminate`, { date: today(), reason: "Terminated from workspace" }), `${r.id} terminated`),
    Reinstate: async () => {
      throw new Error("Reinstating a terminated worker is a rehire - create the worker again.");
    },
  },
};

const lvStatus = (s: string): string => (s === "Draft" ? "Submitted" : s);
const leave: Binding = {
  load: async (c) =>
    (await api.all("api/workforce/leave-requests")).map((e) => row(e, S(e.number), { employee: wName(c, e.workerId), type: S(byId(c.leaveTypes, e.leaveTypeId)?.name), from: D(e.startDate), to: D(e.endDate), status: lvStatus(S(e.status)) })),
  create: async (v, c) => {
    const w = byName(c.workers, v.employee, "fullName");
    const lt = byName(c.leaveTypes, v.type);
    if (!w || !lt) throw new Error("Choose an employee and a leave type.");
    const l = await api.post("api/workforce/leave-requests", { workerId: w.id, leaveTypeId: lt.id, startDate: S(v.from), endDate: S(v.to) });
    await api.post(`api/workforce/leave-requests/${l.id}/submit`).catch(() => undefined);
  },
  remove: async (r) => api.del(`api/workforce/leave-requests/${gid(r)}`),
  act: {
    Approve: async (r) => (await api.post(`api/workforce/leave-requests/${gid(r)}/approve`, { comments: "" }), `${r.id} approved`),
    Reject: async (r) => (await api.post(`api/workforce/leave-requests/${gid(r)}/reject`, { comments: "Rejected" }), `${r.id} rejected`),
  },
};

const woStatus = (s: string): string => ({ Requested: "Open", Approved: "Open", Planned: "Open", InProgress: "In progress", Completed: "Completed", Closed: "Completed" } as Record<string, string>)[s] ?? s;
const maint: Binding = {
  load: async () => (await api.all("api/assets/work-orders")).map((e) => row(e, S(e.number), { asset: S(e.assetName) || S(e.assetCode), issue: S(e.title), priority: S(e.priority), date: D(e.scheduledStart) || D(e.createdAt), amount: N(e.estimatedCost), status: woStatus(S(e.status)) })),
  create: async (v) => {
    const a = (await api.all("api/assets/assets")).find((x) => S(x.name) === S(v.asset) || S(x.code) === S(v.asset));
    if (!a) throw new Error("Choose an asset.");
    await api.post("api/assets/work-orders", { type: "Corrective", priority: S(v.priority) || "Normal", assetId: a.id, siteId: a.siteId, title: S(v.issue), estimatedCost: N(v.amount), scheduledStart: `${S(v.date)}T08:00:00Z` });
  },
  act: {
    "Start work": async (r) => {
      const id = gid(r);
      let e = await api.get(`api/assets/work-orders/${id}`);
      if (S(e.status) === "Requested") e = await api.post(`api/assets/work-orders/${id}/approve`, { comments: "" });
      if (S(e.status) === "Approved") e = await api.post(`api/assets/work-orders/${id}/plan`, { autoDispatch: false });
      await api.post(`api/assets/work-orders/${id}/start`);
      return `${r.id} started`;
    },
    Complete: async (r) => (await api.post(`api/assets/work-orders/${gid(r)}/complete`, { force: false }), `${r.id} completed`),
  },
};

const legal: Binding = {
  load: async () => (await api.all("api/masterdata/companies")).map((e) => row(e, S(e.code), { code: S(e.code), name: S(e.name), country: S(e.countryCode), currency: S(e.baseCurrency), status: e.isActive === false ? "Inactive" : "Active" })),
  create: async (v) => {
    await api.post("api/masterdata/companies", { code: S(v.code), name: S(v.name), legalName: S(v.name), baseCurrency: S(v.currency) || "NGN", countryCode: S(v.country) || "NG", defaultCulture: "en-NG", timeZoneId: "Africa/Lagos", isActive: true });
  },
  update: async (r, v) => void (await change("api/masterdata/companies", gid(r), (e) => Object.assign(e, { name: S(v.name), legalName: S(v.name), countryCode: S(v.country), baseCurrency: S(v.currency) }))),
  remove: async (r) => api.del(`api/masterdata/companies/${gid(r)}`),
  act: {
    Deactivate: async (r) => (await change("api/masterdata/companies", gid(r), (e) => (e.isActive = false)), `${r.id} deactivated`),
    Activate: async (r) => (await change("api/masterdata/companies", gid(r), (e) => (e.isActive = true)), `${r.id} activated`),
  },
};

const units: Binding = {
  load: async (c) => (await api.all("api/workforce/org-units")).map((e) => row(e, S(e.code), { name: S(e.name), type: S(e.type), manager: wName(c, e.managerWorkerId) })),
  create: async (v, c) => {
    const m = byName(c.workers, v.manager, "fullName");
    await api.post("api/workforce/org-units", { code: nextCode("OU-", (await api.all("api/workforce/org-units")).map((u) => S(u.code))), name: S(v.name), type: S(v.type) || "Department", managerWorkerId: m?.id ?? null, siteId: c.site, effectiveFrom: today() });
  },
};

/* =========================================================
   Administration: number sequences and audit log (served by every module for its own schema)
   ========================================================= */
const ALL_MODULES = ["ai", "assets", "clm", "commerce", "controltower", "cpq", "extensibility", "industry", "insights", "intercompany", "inventory", "manufacturing", "masterdata", "pim", "planning", "plm", "procurement", "projects", "quality", "sales", "sustainability", "trade", "warehouse", "workforce"];
const eachModule = async <T>(fn: (m: string) => Promise<T[]>): Promise<T[]> => (await Promise.all(ALL_MODULES.map((m) => fn(m).catch(() => [] as T[])))).flat();

const numseq: Binding = {
  noCreate: "Number sequences are created by the module the first time it numbers a record.",
  load: async () =>
    (await eachModule((m) => api.get<Rec[]>(`api/${m}/admin/number-sequences`))).map((e) => row(e, `${S(e.module)}:${S(e.key)}`, { name: `${S(e.module)} - ${S(e.key)}`, pfx: S(e.key), next: N(e.next) })),
};

const audit: Binding = {
  load: async () => {
    const all = await eachModule(async (m) => (await api.get<{ items: Rec[] }>(`api/${m}/admin/audit-log?pageSize=100`)).items);
    return all
      .sort((a, b) => S(b.at).localeCompare(S(a.at)))
      .slice(0, 400)
      .map((e) => row(e, S(e.id), { when: S(e.at).slice(0, 19).replace("T", " "), user: S(e.userId), entity: `${S(e.module)} / ${S(e.entity)}`, record: S(e.entityId).slice(0, 8), action: S(e.action), detail: S(e.changes).slice(0, 160) }));
  },
};

export const BINDINGS: Record<string, Binding> = {
  ...ACC_BINDINGS,
  vendors: partners("Supplier"),
  customers: partners("Customer"),
  reqs,
  rfqs,
  pos,
  quotes,
  sos,
  returns,
  prices,
  inventory,
  warehouses: whs,
  stock_transfers: stockTransfers,
  counts,
  picks,
  boms,
  prod_orders: prodOrders,
  projects,
  timesheets,
  workers,
  leave,
  maint,
  legal,
  units,
  numseq,
  audit,
};

/** reference data every binding needs, fetched once per refresh */
export async function loadCtx(): Promise<Ctx> {
  const [partnersL, items, locations, workersL, leaveTypes, sites] = await Promise.all([
    api.all("api/masterdata/partners"),
    api.all("api/masterdata/items"),
    api.all("api/masterdata/locations"),
    api.all("api/workforce/workers").catch(() => [] as Rec[]),
    api.all("api/workforce/leave-types").catch(() => [] as Rec[]),
    api.all("api/masterdata/sites"),
  ]);
  const site = sites.find((s) => S(s.code) === "LAG") ?? sites[0];
  return { site: S(site?.id), partners: partnersL, items, locations, workers: workersL, leaveTypes };
}